import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCards, Navigation } from 'swiper/modules';
import { motion, AnimatePresence } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/effect-cards';

import vid1 from '../../assets/site-assets/home/swipe-vid-1.png';
import vid2 from '../../assets/site-assets/home/swipe-vid-2.png';
import vid3 from '../../assets/site-assets/home/swipe-vid-3.png';
import vid4 from '../../assets/site-assets/home/swipe-vid-4.png';
import vid5 from '../../assets/site-assets/home/swipe-vid-5.png';
import vid6 from '../../assets/site-assets/home/swipe-vid-6.png';
import './StorySection.scss';

const storyImageMap = {
  'swipe-vid-1': vid1,
  'swipe-vid-2': vid2,
  'swipe-vid-3': vid3,
  'swipe-vid-4': vid4,
  'swipe-vid-5': vid5,
  'swipe-vid-6': vid6,
};

const PlayIcon = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
    <circle cx="28" cy="28" r="28" fill="rgba(0,0,0,0.45)" />
    <polygon points="22,18 42,28 22,38" fill="#fff" />
  </svg>
);

const StorySection = () => {
  const { t, i18n } = useTranslation();
  const storyCount = Number(t('storySection.count')) || 0;
  const [activeIdx, setActiveIdx] = useState(0);
  const [modalId,   setModalId]   = useState(null);

  const openVideo = (e, id) => {
    e.stopPropagation();
    setModalId(id);
  };

  return (
    <>
      <section className="story-section">
        <div className="story-section__container">

          {/* ── Left: dynamic text ── */}
          <div className="story-section__left">
            <h2 className="story-section__headline">
              <span className="story-section__headline--orange">{t('storySection.headlineHighlight')}</span>
              <br />
              {t('storySection.headlineMain')}
            </h2>

            <p className="story-section__eyebrow">
              {t('storySection.eyebrowLine1')}<br />{t('storySection.eyebrowLine2')}
            </p>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeIdx}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
              >
                <h3 className="story-section__title">
                  {t(`storySection.stories.${activeIdx}.title`)}
                </h3>
                <p className="story-section__desc">{t(`storySection.stories.${activeIdx}.desc`)}</p>
              </motion.div>
            </AnimatePresence>

            <div className="story-section__actions">
              <button
                className="story-section__btn story-section__btn--primary"
                onClick={() => window.open(`https://www.youtube.com/watch?v=${t(`storySection.stories.${activeIdx}.videoId`)}`, '_blank', 'noopener,noreferrer')}
              >
                {t('storySection.watch')}
              </button>
            </div>
          </div>

          {/* ── Right: coverflow swiper ── */}
          <div className="story-section__right">
            <Swiper
              effect="cards"
              cardsEffect={{
                slideShadows: false,
                rotate: false,
                perSlideRotate: 0,
                perSlideOffset: 28,
                perSlideScale: 0.9,
              }}
              loop
              speed={500}
              navigation={{
                prevEl: '.story-section__arrow--prev',
                nextEl: '.story-section__arrow--next',
              }}
              modules={[EffectCards, Navigation]}
              onRealIndexChange={(swiper) => setActiveIdx(swiper.realIndex)}
              key={i18n.language}
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
              className="story-section__swiper"
            >
              <button className="story-section__arrow story-section__arrow--prev" aria-label={t('storySection.previous')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={i18n.language === 'ar' ? { transform: 'scaleX(-1)' } : undefined}>
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button className="story-section__arrow story-section__arrow--next" aria-label={t('storySection.next')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={i18n.language === 'ar' ? { transform: 'scaleX(-1)' } : undefined}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              {Array.from({ length: storyCount }, (_, i) => i).map((i) => (
                <SwiperSlide key={i} className="story-section__slide">
                  <img src={storyImageMap[t(`storySection.stories.${i}.imgSrc`)]} alt={t(`storySection.stories.${i}.imgAlt`)} />
                  <div className="story-section__play-overlay">
                    <button
                      className="story-section__play-btn"
                      onClick={(e) => openVideo(e, t(`storySection.stories.${i}.videoId`))}
                      aria-label={t('storySection.playStory', { title: t(`storySection.stories.${i}.title`) })}
                    >
                      <PlayIcon />
                    </button>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      {/* ── YouTube modal ── */}
      {modalId && (
        <div className="story-modal" onClick={() => setModalId(null)}>
          <motion.div
            className="story-modal__inner"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.22 }}
          >
            <button
              className="story-modal__close"
              onClick={() => setModalId(null)}
              aria-label={t('storySection.closeVideo')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="story-modal__frame">
              <iframe
                src={`https://www.youtube.com/embed/${modalId}?autoplay=1`}
                title={t('storySection.videoTitle')}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default StorySection;
